<template>
    <flex-container :gap="Size.lg">
        <flex-container justify-content="space-between" align-items="center">
            <flex-container size="auto" :gap="Size.xs">
                <text-heading>
                    Document templates
                </text-heading>
            </flex-container>

            <flex-container size="auto" justify-content="flex-end">
                <button-collapse :options="createTemplateOptions" @click="createTemplate">
                    <template #default>
                        Template toevoegen
                    </template>
                </button-collapse>
            </flex-container>
        </flex-container>

        <inline-tabs selected-name="all">
            <template #labels>
                <inline-tab-label
                    v-for="(group, groupKey) in groupedTemplates"
                    :key="groupKey"
                    :name="groupKey"
                >
                    <chip-container>
                        <template v-if="groupKey === 'all'">
                            Alle
                        </template>
                        <template v-else-if="groupKey === 'brochure'">
                            Woningbrochure
                        </template>
                        <template v-else-if="group.type">
                            {{ group.type.label }}
                        </template>

                        <chip-informative small>
                            {{ group.templates.length }}
                        </chip-informative>
                    </chip-container>
                </inline-tab-label>
            </template>

            <template #panels>
                <template-list-tab-panel
                    v-for="(group, groupKey) in groupedTemplates"
                    :key="groupKey"
                    :name="groupKey"
                    :templates="group.templates"
                    @edit-template="editTemplate"
                    @delete-template="deleteTemplate"
                >
                    <template #empty-message>
                        <template v-if="groupKey === 'all'">
                            Er zijn geen templates gevonden
                        </template>
                        <template v-else>
                            Er zijn geen templates gevonden van het gekozen type
                        </template>
                    </template>
                </template-list-tab-panel>
            </template>
        </inline-tabs>

        <div class="custom-template-hint">
            <div>
                <component :is="IllustrationCustomTemplate" style="width: 80px" />
            </div>
            <div>
                <text-decorator bold>
                    Heb je een volledig uniek document nodig dat op maat is ontworpen?
                </text-decorator>
                <div>
                    Dat is mogelijk vanaf slechts EUR 295,-. Bekijk onze MaqR diensten voor meer details.
                </div>
            </div>
        </div>
    </flex-container>

    <confirmation-dialog
        ref="deleteConfirmationDialog"
        label="Template verwijderen"
        description="Weet je zeker dat je deze template wilt verwijderen?"
    >
        <template #default>
            <flex-container :gap="Size.md">
                <text-heading>Template verwijderen</text-heading>
                <p>
                    Weet je zeker dat je deze template wilt verwijderen?
                </p>
            </flex-container>
        </template>

        <template #actions>
            <button-outline @click="onDeleteCancel">
                Annuleren
            </button-outline>
            <button-block @loading="onDeleteConfirm">
                Bevestig
            </button-block>
        </template>
    </confirmation-dialog>
</template>

<script setup lang="ts">
    import {
        ButtonBlock,
        ButtonCollapse,
        type ButtonCollapseOption,
        ButtonOutline,
        ButtonState,
        type ButtonStateChangedEvent,
        ChipContainer,
        ChipInformative,
        ConfirmationDialog,
        type ExposedOverlay,
        FlexContainer,
        InlineTabLabel,
        InlineTabs,
        Size,
        TextDecorator,
        TextHeading,
    } from '@keyvue/one-ui';

    import TemplateListTabPanel from '@views/Document/Template/List/TemplateListTabPanel.vue';

    import {
        Template as BrochureTemplate,
        DocumentTemplate,
        DocumentType,
        DeleteTemplateCommand as DeleteBrochureTemplateCommand,
        DeleteDocumentTemplateCommand,
        Layout,
    } from '@keyvue/one-api-client';

    import { computed, ref } from 'vue';
    import { type RouteLocationRaw, useRouter } from 'vue-router';
    import useClient from '@composables/useClient';
    import useNotify from '@composables/useNotify';

    import IllustrationCustomTemplate from '@/assets/illustrations/customTemplate.svg?component';

    const router = useRouter();
    const { notifyError, notifyDeleted } = useNotify();
    const { client, getReactiveCollection } = useClient();

    const [
        layoutCollection,
        brochureTemplateCollection,
        documentTemplateCollection,
    ] = await Promise.all([
        getReactiveCollection(Layout, { active: true, editable: true }),
        getReactiveCollection(BrochureTemplate, { 'order[updatedAt]': 'desc' }),
        getReactiveCollection(DocumentTemplate, { 'order[updatedAt]': 'desc' }),
    ]);

    const availableDocumentTypes = computed(() => [
        ...new Set(
            layoutCollection.data.value.map((layout) => layout.type.value),
        ),
    ]);

    const templates = computed(() => {
        const result = [
            ...brochureTemplateCollection.data.value,
            ...documentTemplateCollection.data.value,
        ];

        result.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -0));

        return result;
    });

    interface GroupedTemplate {
        type: DocumentType | null,
        templates: Array<BrochureTemplate | DocumentTemplate>,
    }

    const groupedTemplates = computed((): Record<string, GroupedTemplate> => {
        const result = {
            all: {
                type: null,
                templates: templates.value,
            },
            brochure: {
                type: null,
                templates: brochureTemplateCollection.data.value,
            },
        };

        DocumentType.values.forEach((documentType) => {
            result[documentType.value] = {
                type: documentType,
                templates: documentTemplateCollection.data.value.filter((template) => template.layout.type.equals(documentType)),
            };
        });

        return result;
    });

    const createTemplateOptions = computed(() => {
        const result: ButtonCollapseOption[] = [
            {
                value: 'brochure',
                label: 'Woningbrochure',
            },
        ];

        DocumentType.values.forEach((documentType) => {
            result.push({
                value: documentType.value,
                label: documentType.label,
                disabled: !availableDocumentTypes.value.includes(documentType.value),
            });
        });

        return result;
    });

    const createTemplate = (documentType: string) => {
        let route: RouteLocationRaw = {
            name: 'documentTemplateCreate',
            params: {
                type: documentType,
            },
        };

        if (documentType === 'brochure') {
            route = {
                name: 'documentTemplateCreateBrochure',
            };
        }

        router.push(route);
    };

    const editTemplate = (template: BrochureTemplate | DocumentTemplate) => {
        let routeName = 'documentTemplateEdit';
        if (template instanceof BrochureTemplate) {
            routeName = 'documentTemplateEditBrochure';
        }

        router.push({
            name: routeName,
            params: {
                templateId: template.id.value,
            },
        });
    };

    const deleteConfirmationDialog = ref<ExposedOverlay>();
    const deleteConfirmationDialogTemplate = ref<BrochureTemplate | DocumentTemplate | null>();

    const deleteTemplate = (template: BrochureTemplate | DocumentTemplate) => {
        deleteConfirmationDialogTemplate.value = template;
        deleteConfirmationDialog.value?.openOverlay();
    };

    const onDeleteCancel = () => {
        deleteConfirmationDialog.value?.closeOverlay();
        deleteConfirmationDialogTemplate.value = null;
    };

    const onDeleteConfirm = async (event: ButtonStateChangedEvent) => {
        const template = deleteConfirmationDialogTemplate.value;
        if (!template) {
            return;
        }

        let deleteTemplateCommand;
        if (template instanceof BrochureTemplate) {
            deleteTemplateCommand = DeleteBrochureTemplateCommand.fromTemplate(template);
        } else {
            deleteTemplateCommand = DeleteDocumentTemplateCommand.fromTemplate(template);
        }

        try {
            await client.dispatch(deleteTemplateCommand);
            await Promise.all([
                brochureTemplateCollection.load(),
                documentTemplateCollection.load(),
            ]);
            notifyDeleted();
            deleteConfirmationDialogTemplate.value = null;
            deleteConfirmationDialog.value?.closeOverlay();
        } catch (e) {
            notifyError(e);
        } finally {
            event.button.setState(ButtonState.completed);
        }
    };
</script>

<style scoped lang="scss">
.custom-template-hint {
    display: flex;
    border: dashed 2px #e8bcca;
    border-radius: 2rem;
    padding: 1rem;
    align-items: center;
    margin: 0 auto;
    gap: 1rem;
}
</style>
